import { useEffect, useMemo, useState } from "react";
import { ArrowRight, Check, CreditCard, Info, Loader2, LockKeyhole, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { useSite } from "@/context/SiteContext";

const QUICK_AMOUNTS = [25, 50, 100, 250];
const PURPOSES = [
  "General Donation",
  "Medication Assistance",
  "Community Awareness & Screening",
  "Patient Education & Support",
  "Emerald Honours",
];

function makeReference() {
  const day = new Date().toISOString().slice(0, 10).replace(/-/g, "");
  return `BKA-${day}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
}

export default function Donate() {
  const { s } = useSite();
  const rawPhone = String(s("site.phone") || "").trim();
  const rawEmail = String(s("site.email") || "").trim();
  const publicPhone = !rawPhone || rawPhone === "(242) 123-4567" ? "+1 242-436-1317" : rawPhone;
  const publicEmail = !rawEmail || rawEmail === "info@bahamaskidney.org"
    ? "info@bahamaskidneyassociation.org"
    : rawEmail;
  const [state, setState] = useState({ loading: true, configured: false, currency: "BSD" });
  const [amount, setAmount] = useState("50.00");
  const [purpose, setPurpose] = useState(PURPOSES[0]);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [redirecting, setRedirecting] = useState(false);
  const reference = useMemo(() => makeReference(), []);

  useEffect(() => {
    api
      .get("/public/donate")
      .then((r) => setState({ loading: false, ...r.data }))
      .catch(() => setState({ loading: false, configured: false, currency: "BSD" }));
  }, []);

  const go = async () => {
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError("Please enter a donation amount greater than $0.");
      return;
    }
    setError("");
    setRedirecting(true);
    try {
      const { data } = await api.post("/public/donate/checkout", {
        amount: parsed,
        purpose,
        note: note.trim(),
        reference,
      });
      if (!data?.url) throw new Error("Missing checkout URL");
      window.location.href = data.url;
    } catch (e) {
      setRedirecting(false);
      setError(e?.response?.data?.detail || "The secure donation checkout is not available yet. Please contact BKA.");
    }
  };

  return (
    <>
      <Seo
        title="Donate | Bahamas Kidney Association"
        description="Support kidney health education, screening and medication assistance across The Bahamas."
        canonicalPath="/donate"
      />

      <section className="border-b border-slate-200 bg-slate-50" data-testid="donate-page">
        <div className="bka-container grid min-h-[calc(100vh-74px)] lg:grid-cols-[0.82fr_1.18fr]">
          <aside className="border-b border-slate-200 px-0 py-12 lg:border-b-0 lg:border-r lg:py-16 lg:pr-12">
            <p className="bka-overline text-bka-orangeDark">Support Kidney Health</p>
            <h1 className="mt-3 max-w-md font-display text-5xl font-bold leading-[0.98] text-bka-navy sm:text-6xl">
              Make a<br />Contribution
            </h1>
            <p className="mt-7 max-w-md text-base leading-7 text-bka-ink/75">
              Choose your amount and what you would like to support. You will then continue to BKA's secure
              Fygaro checkout to enter your contact and payment details.
            </p>

            <ol className="mt-8 space-y-4">
              {["Enter amount", "Choose what to support", "Pay securely with Fygaro"].map((label, index) => (
                <li key={label} className="flex items-center gap-3 text-sm font-bold text-bka-navy">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-bka-emerald text-white">
                    {index + 1}
                  </span>
                  {label}
                </li>
              ))}
            </ol>

            <div className="mt-10 max-w-md rounded-2xl border border-emerald-100 bg-white p-5">
              <div className="flex gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-bka-emerald" />
                <p className="text-sm leading-6 text-bka-ink/70">
                  BKA does not collect or store card numbers, security codes or payment credentials on this website.
                  Payment details are entered directly on Fygaro's secure hosted checkout.
                </p>
              </div>
            </div>
          </aside>

          <div className="bg-white px-0 py-12 lg:px-12 lg:py-16">
            {state.loading ? (
              <div className="flex min-h-[300px] items-center justify-center gap-3 text-sm font-semibold text-bka-ink/60">
                <Loader2 className="h-5 w-5 animate-spin" /> Loading secure donation options…
              </div>
            ) : state.configured ? (
              <div className="mx-auto max-w-2xl space-y-7" data-testid="donate-ready">
                <div>
                  <label htmlFor="donation-amount" className="text-sm font-bold text-bka-navy">Contribution amount</label>
                  <p className="mt-1 text-sm text-bka-ink/55">Enter the amount you wish to give. Currency: <strong>{state.currency || "BSD"}</strong>.</p>
                  <div className="relative mt-3">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-bold text-bka-emerald">$</span>
                    <input
                      id="donation-amount"
                      data-testid="donate-amount"
                      inputMode="decimal"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                      className="h-14 w-full rounded-2xl border border-slate-300 bg-white pl-9 pr-4 text-lg font-bold text-bka-navy outline-none transition focus:border-bka-emerald focus:ring-4 focus:ring-emerald-50"
                      aria-describedby={error ? "donation-error" : undefined}
                    />
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {QUICK_AMOUNTS.map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setAmount(v.toFixed(2))}
                        className="rounded-full border border-slate-300 px-4 py-2 text-sm font-bold text-bka-navy hover:border-bka-emerald hover:bg-emerald-50"
                      >
                        ${v}
                      </button>
                    ))}
                  </div>
                  {error && <p id="donation-error" className="mt-2 text-sm font-semibold text-red-600">{error}</p>}
                </div>

                <div>
                  <label htmlFor="donation-purpose" className="text-sm font-bold text-bka-navy">What would you like to support?</label>
                  <select
                    id="donation-purpose"
                    data-testid="donate-purpose"
                    value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                    className="mt-3 h-14 w-full rounded-2xl border border-slate-300 bg-white px-4 text-base font-semibold text-bka-navy outline-none focus:border-bka-emerald focus:ring-4 focus:ring-emerald-50"
                  >
                    {PURPOSES.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>

                <div>
                  <label htmlFor="donation-note" className="text-sm font-bold text-bka-navy">Note <span className="font-normal text-bka-ink/50">(optional)</span></label>
                  <textarea
                    id="donation-note"
                    data-testid="donate-note"
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, 240))}
                    rows={3}
                    placeholder="Any information BKA should know about this contribution."
                    className="mt-3 w-full rounded-2xl border border-slate-300 bg-white p-4 text-sm text-bka-navy outline-none focus:border-bka-emerald focus:ring-4 focus:ring-emerald-50"
                  />
                </div>

                <div>
                  <p className="text-sm font-bold text-bka-navy">Payment method</p>
                  <div className="mt-3 rounded-2xl border-2 border-bka-emerald bg-emerald-50/30 p-5">
                    <div className="flex items-start gap-4">
                      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-bka-emerald shadow-sm">
                        <CreditCard className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-3">
                          <p className="font-bold text-bka-navy">Fygaro Secure Checkout</p>
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-bka-emerald text-white">
                            <Check className="h-4 w-4" />
                          </span>
                        </div>
                        <p className="mt-1 text-sm leading-6 text-bka-ink/65">
                          Cardholder and payment details are completed securely on Fygaro.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl bg-slate-50 p-4 text-xs leading-5 text-bka-ink/55">
                  <span className="font-bold text-bka-navy">BKA reference:</span> {reference}
                </div>

                <button
                  type="button"
                  data-testid="donate-continue-button"
                  onClick={go}
                  disabled={redirecting}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-bka-orange px-6 py-4 text-base font-bold text-white shadow-[0_14px_32px_-16px_rgba(143,55,3,0.95)] transition hover:bg-bka-orangeDark disabled:opacity-70"
                >
                  {redirecting ? <><Loader2 className="h-5 w-5 animate-spin" /> Redirecting securely…</> : <><LockKeyhole className="h-5 w-5" /> Continue to Secure Payment <ArrowRight className="h-5 w-5" /></>}
                </button>

                <p className="text-center text-xs leading-5 text-bka-ink/50">
                  By continuing, the amount is securely signed by BKA's server and you will complete payment on Fygaro's hosted checkout.
                </p>
              </div>
            ) : (
              <div className="mx-auto max-w-xl rounded-3xl border-l-4 border-bka-orange bg-bka-cream p-8" data-testid="donate-not-configured">
                <Info className="h-6 w-6 text-bka-orangeDark" />
                <h2 className="mt-5 font-display text-2xl font-bold text-bka-navy">Online giving is being finalised</h2>
                <p className="mt-3 text-base leading-7 text-bka-ink/75">
                  The secure Fygaro donation link has not yet been added. Please contact the Bahamas Kidney Association and a member of the team will assist you.
                </p>
                <div className="mt-6 space-y-2 text-sm font-semibold text-bka-navy">
                  <p>
                    Phone:{" "}
                    <a className="underline decoration-bka-emerald/40 underline-offset-4" href={`tel:${publicPhone.replace(/\s+/g, "")}`}>
                      {publicPhone}
                    </a>
                  </p>
                  <p>
                    Email:{" "}
                    <a className="underline decoration-bka-emerald/40 underline-offset-4" href={`mailto:${publicEmail}`}>
                      {publicEmail}
                    </a>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
